/**
 * useJurisdictions
 *
 * Fetches the org-entitled jurisdiction tree (or the flat admin list) from
 * GET /api/jurisdictions/tree.
 *
 * For SYSTEM_ADMINs the server returns a flat list of all jurisdictions.
 * For everyone else it returns only the entitled subtree with frozen flags.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import type { Jurisdiction } from '@/types/database';

export type { Jurisdiction };

/** Flat list derived from a tree (depth-first) */
export function flattenTree(nodes: Jurisdiction[]): Jurisdiction[] {
  const result: Jurisdiction[] = [];
  const walk = (list: Jurisdiction[]) => {
    for (const n of list) {
      result.push(n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return result;
}

/** Human-readable indent label, e.g. "  ↳ City of Riverside" */
export function indentLabel(
  j: Jurisdiction,
  depthMap: Map<string, number>
): string {
  const depth = depthMap.get(j.id) ?? 0;
  const prefix = depth > 0 ? '  '.repeat(depth) + '↳ ' : '';
  return `${prefix}${j.name}${j.frozen ? ' (lapsed)' : ''}`;
}

export function buildDepthMap(nodes: Jurisdiction[]): Map<string, number> {
  const map = new Map<string, number>();
  const walk = (list: Jurisdiction[], depth: number) => {
    for (const n of list) {
      map.set(n.id, depth);
      if (n.children?.length) walk(n.children, depth + 1);
    }
  };
  walk(nodes, 0);
  return map;
}

export function useJurisdictions() {
  const [tree, setTree] = useState<Jurisdiction[]>([]);
  const [flat, setFlat] = useState<Jurisdiction[]>([]);
  const [depthMap, setDepthMap] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJurisdictions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/jurisdictions/tree');
      if (!res.ok)
        throw new Error(`Failed to load jurisdictions: ${res.status}`);
      const json = await res.json();
      const data = (json.data ?? []) as Jurisdiction[];
      setTree(data);
      const f = flattenTree(data);
      setFlat(f);
      setDepthMap(buildDepthMap(data));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load jurisdictions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchJurisdictions();
  }, [fetchJurisdictions]);

  /** Create a new jurisdiction (SYSTEM_ADMIN only) */
  const createJurisdiction = async (payload: {
    name: string;
    level: string;
    parent_id?: string | null;
    path?: string | null;
    external_ids?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
  }) => {
    const res = await apiFetch('/api/jurisdictions', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(
        (e as { error?: string }).error ?? 'Failed to create jurisdiction'
      );
    }
    const json = await res.json();
    await fetchJurisdictions(); // refresh
    return json.data as Jurisdiction;
  };

  /** Update a jurisdiction (SYSTEM_ADMIN only) */
  const updateJurisdiction = async (
    id: string,
    patch: Partial<Jurisdiction>
  ) => {
    const res = await apiFetch(`/api/jurisdictions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(
        (e as { error?: string }).error ?? 'Failed to update jurisdiction'
      );
    }
    const json = await res.json();
    await fetchJurisdictions();
    return json.data as Jurisdiction;
  };

  return {
    tree,
    flat,
    depthMap,
    loading,
    error,
    fetchJurisdictions,
    createJurisdiction,
    updateJurisdiction,
  };
}

/** Lightweight hook — just returns a flat select-friendly list. */
export function useJurisdictionOptions() {
  const { flat, depthMap, loading, error } = useJurisdictions();
  return { options: flat, depthMap, loading, error };
}
